/**
 * ExpenseAggregationDetailPage Component
 * Shows aggregation details, member expenses, AI classification, and management actions.
 * Route: /expense-aggregations/:id
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { aggregationsApi, expensesApi } from '../lib/api';
import { PageHeader } from '../components/layout/index';
import { ExpensesLayout } from '../components/expenses/ExpensesLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import type { AggregationDetail, Expense } from '../types/expense.types';
import { formatAmount, formatExpenseDate } from '../utils/expense.utils';

function ClassificationBadge({ type, aiSuggested }: { type: string | null; aiSuggested: string | null }) {
  if (!type && !aiSuggested) {
    return <Badge variant="secondary">Unclassified</Badge>;
  }
  return (
    <Badge variant="secondary" className="bg-primary/10 text-primary">
      {type ?? aiSuggested}
      {aiSuggested && aiSuggested !== type && (
        <span className="text-xs text-muted-foreground ml-1">
          (AI: {aiSuggested})
        </span>
      )}
    </Badge>
  );
}

export function ExpenseAggregationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [aggregation, setAggregation] = useState<AggregationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isClassifying, setIsClassifying] = useState(false);
  const [classifyError, setClassifyError] = useState<string | null>(null);

  const [manualClassification, setManualClassification] = useState<string>('');
  const [isSavingClassification, setIsSavingClassification] = useState(false);
  const [classificationSaveError, setClassificationSaveError] = useState<string | null>(null);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  const [removingExpenseId, setRemovingExpenseId] = useState<string | null>(null);

  // Add expenses
  const [availableExpenses, setAvailableExpenses] = useState<Expense[]>([]);
  const [isAddingExpenses, setIsAddingExpenses] = useState(false);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<string[]>([]);
  const [addError, setAddError] = useState<string | null>(null);
  const [isSubmittingAdd, setIsSubmittingAdd] = useState(false);

  const fetchAggregation = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const result = await aggregationsApi.get(id);
      setAggregation(result);
      setNameValue(result.name);
      setManualClassification(result.aggregationType ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load aggregation');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAggregation();
  }, [fetchAggregation]);

  const handleSaveName = async () => {
    if (!id || !aggregation) return;
    setIsSavingName(true);
    try {
      const result = await aggregationsApi.update(id, { name: nameValue });
      setAggregation(result);
      setIsEditingName(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update name');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleClassify = async () => {
    if (!id) return;
    setIsClassifying(true);
    setClassifyError(null);
    try {
      const result = await aggregationsApi.classify(id);
      setAggregation(result);
    } catch (err) {
      setClassifyError(err instanceof Error ? err.message : 'Classification failed');
    } finally {
      setIsClassifying(false);
    }
  };

  const handleRemoveExpense = async (expenseId: string) => {
    if (!id) return;
    setRemovingExpenseId(expenseId);
    try {
      await aggregationsApi.removeExpense(id, expenseId);
      await fetchAggregation();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove expense');
    } finally {
      setRemovingExpenseId(null);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await aggregationsApi.delete(id);
      navigate(`/properties/${aggregation?.propertyId ?? ''}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete aggregation');
      setIsDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClassificationChange = async (value: string) => {
    if (!id || !value) return;
    setManualClassification(value);
    setIsSavingClassification(true);
    setClassificationSaveError(null);
    try {
      const result = await aggregationsApi.setClassification(
        id,
        value as 'capital_improvement' | 'repair' | 'move_out' | 'other'
      );
      setAggregation(result);
    } catch (err) {
      setClassificationSaveError(err instanceof Error ? err.message : 'Failed to save classification');
      // Revert to original value on failure
      setManualClassification(aggregation?.aggregationType ?? '');
    } finally {
      setIsSavingClassification(false);
    }
  };

  const handleOpenAddExpenses = async () => {
    if (!aggregation) return;
    setAddError(null);
    setSelectedExpenseIds([]);
    setIsAddingExpenses(true);
    try {
      const result = await expensesApi.list({ propertyId: aggregation.propertyId });
      // Filter out expenses already in this aggregation
      const memberIds = new Set(aggregation.expenses.map((e) => e.id));
      setAvailableExpenses(result.expenses.filter((e) => !memberIds.has(e.id)));
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to load expenses');
    }
  };

  const handleAddExpenses = async () => {
    if (!id || selectedExpenseIds.length === 0) return;
    setIsSubmittingAdd(true);
    setAddError(null);
    try {
      const result = await aggregationsApi.addExpenses(id, selectedExpenseIds);
      setAggregation(result);
      setIsAddingExpenses(false);
      setSelectedExpenseIds([]);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add expenses');
    } finally {
      setIsSubmittingAdd(false);
    }
  };

  const toggleExpenseSelection = (expenseId: string) => {
    setSelectedExpenseIds((prev) =>
      prev.includes(expenseId) ? prev.filter((id) => id !== expenseId) : [...prev, expenseId]
    );
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] pt-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </div>
        <div className="px-[var(--page-padding-x)] mt-6 space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error && !aggregation) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="text-center py-16 px-6 rounded-lg bg-card">
          <h2 className="text-xl font-semibold mb-3">
            Error Loading Aggregation
          </h2>
          <p className="text-sm text-destructive mb-6">
            {error}
          </p>
          <div className="flex justify-center gap-3">
            <Button onClick={fetchAggregation} size="sm">
              Retry
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!aggregation) return null;

  return (
    <ExpensesLayout>
    <div className="max-w-4xl mx-auto pb-6" data-testid="aggregation-detail-page">
      {/* Header with back button */}
      <div className="px-[var(--page-padding-x)]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="aggregation-back-button"
          aria-label="Go back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
          <span>Back</span>
        </button>
        <PageHeader
          title={aggregation.name}
        />
      </div>

      <div className="px-[var(--page-padding-x)] space-y-4">
        {/* Global error */}
        {error && (
          <div
            role="alert"
            className="p-3 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
          >
            {error}
          </div>
        )}

        {/* Name / Total card */}
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex justify-between items-start gap-4 flex-wrap">
            <div className="flex-1">
              {isEditingName ? (
                <div className="flex gap-2 items-center flex-wrap">
                  <input
                    type="text"
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    className="flex-1 px-3 py-2 text-lg font-semibold border border-input rounded-md outline-none min-h-[44px] bg-background text-foreground"
                    data-testid="aggregation-name"
                    aria-label="Aggregation name"
                    autoFocus
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveName}
                    disabled={isSavingName || !nameValue.trim()}
                  >
                    {isSavingName ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setIsEditingName(false); setNameValue(aggregation.name); }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-wrap">
                  <h1
                    className="m-0 text-2xl font-bold text-foreground"
                    data-testid="aggregation-name"
                  >
                    {aggregation.name}
                  </h1>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditingName(true)}
                    aria-label="Edit aggregation name"
                  >
                    Edit
                  </Button>
                </div>
              )}
              {aggregation.description && (
                <p className="mt-2 m-0 text-sm text-muted-foreground">
                  {aggregation.description}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="m-0 text-xs text-muted-foreground mb-1">
                Total
              </p>
              <p
                className="m-0 text-4xl font-bold text-foreground"
                data-testid="aggregation-total"
                aria-label={`Aggregation total: ${formatAmount(aggregation.totalAmount)}`}
              >
                {formatAmount(aggregation.totalAmount)}
              </p>
            </div>
          </div>
        </div>

        {/* Classification */}
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex justify-between items-center mb-3">
            <h2 className="m-0 text-base font-semibold text-foreground">
              Classification
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClassify}
              disabled={isClassifying}
              data-testid="aggregation-classify-button"
              aria-label="Re-classify with AI"
            >
              {isClassifying ? 'Classifying...' : 'Re-classify with AI'}
            </Button>
          </div>
          <div data-testid="aggregation-type">
            <ClassificationBadge
              type={aggregation.aggregationType}
              aiSuggested={aggregation.aggregationTypeAiSuggested}
            />
          </div>

          {/* AI suggestion reference label (shown when it differs from current value) */}
          {aggregation.aggregationTypeAiSuggested &&
            aggregation.aggregationTypeAiSuggested !== aggregation.aggregationType && (
            <p className="mt-2 m-0 text-xs text-muted-foreground">
              AI suggestion: {aggregation.aggregationTypeAiSuggested}
            </p>
          )}

          {/* Manual override dropdown — saves immediately on change */}
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <label
              htmlFor="aggregation-classification-select"
              className="text-xs font-medium text-foreground"
            >
              Override:
            </label>
            <select
              id="aggregation-classification-select"
              value={manualClassification}
              onChange={(e) => handleClassificationChange(e.target.value)}
              disabled={isSavingClassification}
              className="px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
              data-testid="aggregation-classification-select"
              aria-label="Manual classification override"
            >
              <option value="">-- None (use AI suggestion) --</option>
              <option value="capital_improvement">Capital Improvement</option>
              <option value="repair">Repair</option>
              <option value="move_out">Move-Out</option>
              <option value="other">Other</option>
            </select>
            {isSavingClassification && (
              <span className="text-xs text-muted-foreground">Saving...</span>
            )}
          </div>

          {classificationSaveError && (
            <p role="alert" className="mt-2 m-0 text-xs text-destructive">
              {classificationSaveError}
            </p>
          )}

          {classifyError && (
            <p role="alert" className="mt-2 m-0 text-xs text-destructive">
              {classifyError}
            </p>
          )}
        </div>

        {/* Member Expenses */}
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="m-0 text-base font-semibold text-foreground">
              Member Expenses
              <span className="font-normal text-muted-foreground ml-2">
                ({aggregation.expenseCount})
              </span>
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenAddExpenses}
              data-testid="aggregation-add-expenses"
              aria-label="Add expenses to aggregation"
            >
              + Add Expenses
            </Button>
          </div>

          {/* Add Expenses Panel */}
          {isAddingExpenses && (
            <div className="mb-4 p-4 bg-accent/50 rounded-md border border-border">
              <h3 className="m-0 mb-3 text-sm font-semibold text-foreground">
                Select Expenses to Add
              </h3>
              {addError && (
                <p role="alert" className="m-0 mb-3 text-xs text-destructive">
                  {addError}
                </p>
              )}
              {availableExpenses.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No unlinked expenses available for this property.
                </p>
              ) : (
                <div className="max-h-60 overflow-y-auto mb-3">
                  {availableExpenses.map((expense) => (
                    <label
                      key={expense.id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer rounded ${
                        selectedExpenseIds.includes(expense.id) ? 'bg-primary/10' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedExpenseIds.includes(expense.id)}
                        onChange={() => toggleExpenseSelection(expense.id)}
                        aria-label={`Select expense: ${expense.description}`}
                      />
                      <span className="flex-1 text-sm text-foreground">
                        {formatExpenseDate(expense.expenseDate)} — {expense.description}
                      </span>
                      <span className="text-sm font-semibold text-foreground">
                        {formatAmount(expense.amount)}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleAddExpenses}
                  disabled={isSubmittingAdd || selectedExpenseIds.length === 0}
                  data-testid="aggregation-add-expenses-submit"
                >
                  {isSubmittingAdd ? 'Adding...' : `Add ${selectedExpenseIds.length} Expense${selectedExpenseIds.length !== 1 ? 's' : ''}`}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddingExpenses(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {aggregation.expenses.length === 0 ? (
            <div className="py-6 text-center text-muted-foreground text-sm" data-testid="aggregation-expenses-table">
              No expenses in this aggregation yet.
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="aggregation-expenses-table">
              <table
                className="w-full border-collapse text-sm"
                aria-label="Member expenses"
              >
                <thead>
                  <tr>
                    {['Date', 'Description', 'Vendor', 'Amount', ''].map((col) => (
                      <th
                        key={col}
                        className={`px-3 py-2 border-b-2 border-border text-xs font-semibold text-muted-foreground uppercase tracking-wide ${col === 'Amount' ? 'text-right' : 'text-left'}`}
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {aggregation.expenses.map((expense) => (
                    <tr key={expense.id} className="border-b border-border">
                      <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">
                        {formatExpenseDate(expense.expenseDate)}
                      </td>
                      <td className="px-3 py-3 text-foreground">
                        <Link
                          to={`/expenses/${expense.id}`}
                          className="text-primary no-underline hover:text-primary"
                        >
                          {expense.description}
                        </Link>
                      </td>
                      <td className="px-3 py-3 text-muted-foreground">
                        {expense.vendorName ?? '—'}
                      </td>
                      <td className="px-3 py-3 text-right font-semibold text-foreground whitespace-nowrap">
                        {formatAmount(expense.amount)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleRemoveExpense(expense.id)}
                          disabled={removingExpenseId === expense.id}
                          data-testid={`aggregation-remove-expense-${expense.id}`}
                          aria-label={`Remove expense: ${expense.description}`}
                        >
                          {removingExpenseId === expense.id ? 'Removing...' : 'Remove'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Danger Zone */}
        <div className="rounded-lg border border-destructive/20 bg-card p-4">
          <h2 className="m-0 mb-3 text-base font-semibold text-foreground">
            Danger Zone
          </h2>
          <p className="m-0 mb-4 text-sm text-muted-foreground">
            Deleting this aggregation will unlink all member expenses but will not delete the expense records themselves.
          </p>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setIsDeleteDialogOpen(true)}
            data-testid="aggregation-delete-button"
            aria-label="Delete aggregation"
          >
            Delete Aggregation
          </Button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Aggregation"
        message={`Are you sure you want to delete "${aggregation.name}"? All ${aggregation.expenseCount} member expenses will be unlinked but not deleted.`}
        confirmText="Delete Aggregation"
        isDestructive
        isLoading={isDeleting}
      />
    </div>
    </ExpensesLayout>
  );
}

export default ExpenseAggregationDetailPage;
